/**
 * URL ⇄ catalog-state layer for /library (homepage v2 Phase 1, gap G-15).
 *
 * Until now the library read NO query params — the router matches pathname
 * only and boot state came from localStorage. This module makes deep links
 * like /library?search=super+mario&ach=1 real:
 *
 *   boot:   applyBrowseState (localStorage) → applyUrlParams (URL WINS)
 *           → installBrowsePersistence → installUrlSync
 *   after:  handoff params (search / ach / scores / system) are mirrored to
 *           the query string via history.replaceState on the index route.
 *
 * `search` stays excluded from localStorage persistence (browse-state.js
 * contract) — the URL is its carrier.
 */
import { DEFAULT_FILTERS } from './state.js';
import { parseReleaseTypes, sanitizeReleaseTypes } from './browse-state.js';

/** Parse catalog-affecting query params. Unknown/invalid values are dropped. */
export function parseCatalogParams(queryString, validSystems = []) {
  const p = new URLSearchParams(queryString || '');
  const out = { filters: {} };
  const search = (p.get('search') || '').trim();
  if (search) out.search = search;
  const system = (p.get('system') || '').trim().toLowerCase();
  if (system && (system === 'all' || validSystems.includes(system))) out.system = system;
  if (p.get('ach') === '1') out.filters.achievements = true;
  if (p.get('scores') === '1') out.filters.scores = true;
  const genre = (p.get('genre') || '').trim();
  if (genre) out.filters.genre = genre;
  const letter = (p.get('letter') || '').trim().toUpperCase();
  if (/^[A-Z0-9#]$/.test(letter)) out.filters.letter = letter;
  if (p.get('favorite') === '1' || p.get('favorite') === 'true') out.filters.favorite = true;
  if (p.get('mine') === '1' || p.get('mine') === 'true') out.filters.mine = true;
  const played = p.get('played');
  if (played === 'true' || played === 'false') out.filters.played = played;
  if (p.get('top100') === '1' || p.get('top100') === 'true') out.filters.top100 = true;
  if (p.has('release_types')) {
    const selected = parseReleaseTypes(p.getAll('release_types'));
    if (selected) out.filters.release_types = selected;
  }
  if (Object.keys(out.filters).length === 0) delete out.filters;
  return out;
}

/**
 * Apply URL params to catalog state. Call AFTER applyBrowseState and BEFORE
 * installBrowsePersistence. Returns true when anything was applied.
 *
 * A parameterized URL defines the WHOLE ordinary browse context — system and facets
 * reset to defaults and only what the URL names is applied. The release type
 * selection is independent and stays restored unless the URL names valid types.
 * Merging ordinary facets over the
 * persisted browse state instead would let a stale SNES/genre/facet selection
 * silently narrow a shared link or a search handoff, so the grid would no
 * longer show what the dropdown counted (PR #201 review, finding 1). Sort and
 * view stay persisted — they are count-neutral presentation preferences.
 * A param-less /library keeps the persisted browse state untouched.
 */
export function applyUrlParams(state, queryString, validSystems = []) {
  const parsed = parseCatalogParams(queryString, validSystems);
  if (!parsed.search && !parsed.system && !parsed.filters) return false;
  const cur = state.get().catalog;
  state.set({
    catalog: {
      ...cur,
      search: parsed.search || '',
      system: parsed.system || 'all',
      filters: {
        ...DEFAULT_FILTERS,
        release_types: sanitizeReleaseTypes(cur.filters?.release_types),
        ...(parsed.filters || {}),
      },
    },
  });
  return true;
}

/** The handoff params the header search composes (spec §3.4). */
export function serializeHandoffParams(catalog) {
  const p = new URLSearchParams();
  if (catalog.search) p.set('search', catalog.search);
  const f = catalog.filters || {};
  if (f.achievements) p.set('ach', '1');
  if (f.scores) p.set('scores', '1');
  if (catalog.system && catalog.system !== 'all') p.set('system', catalog.system);
  return p.toString();
}

/** Mirror handoff params into the address bar while on the index route. */
export function installUrlSync(state, { win = window } = {}) {
  return state.subscribe(
    (s) => serializeHandoffParams(s.catalog || {}),
    (qs) => {
      const path = win.location.pathname;
      if (path !== '/library' && path !== '/library/') return;
      win.history.replaceState(win.history.state, '', qs ? `/library?${qs}` : '/library');
    },
  );
}

/** Remove one param from the current URL, preserving all the others. */
export function stripParamFromUrl(win, name) {
  const p = new URLSearchParams(win.location.search);
  if (!p.has(name)) return;
  p.delete(name);
  const qs = p.toString();
  win.history.replaceState(win.history.state, '', win.location.pathname + (qs ? `?${qs}` : ''));
}
